import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import musicianBeforeImg from '../src/assets/musician/musician-before.jpg';
import musicianAfterImg from '../src/assets/musician/musician-after.jpg';
import { ImageUploader } from '../components/ImageUploader';
import { ImageViewer } from '../components/ImageViewer';
import { MusicianControlPanel } from '../components/MusicianControlPanel';
import { LoginModal } from '../components/LoginModal';
import { GalleryModal } from '../components/GalleryModal';
import {
  getMusicianBackgroundById,
  getMusicianDressById,
  getMusicianSleeveById,
  getMusicianItemById,
  getMusicianPoseById,
} from '../constants/musician';
import { useAuth } from '../contexts/AuthContext';
import { generateHeadshot } from '../services/geminiService';
import { updateCreditsAndSaveImage, forceSyncCredits, getShowcaseItemById } from '../services/firebase';
import {
  clearInheritedStyle,
  loadInheritedStyle,
  normalizeInheritedSettings,
  saveInheritedStyle,
  type InheritedStylePayload,
} from '../utils/showcaseStyle';

type Step = 'upload' | 'template' | 'generate' | 'result';
type ImageData = { data: string; mimeType: string } | null;

const MUSICIAN_BASE_PROMPT =
  '被写体はアップロード画像の同一人物。構図は顔と腰の位置までの上半身を中心とし、撮影はプロのポートレート写真のクオリティで行う。音楽家・演奏家向けの宣材写真。カメラは焦点距離85mm相当、F2.8で撮影したような自然なボケ感。ライティングは柔らかいキーライトを45度から当て、補助光で影を整え、瞳にキャッチライトを入れる。メイクは自然で血色を残し、肌は健康的なツヤ。小物は最小限で、主役は被写体本人。仕上げは過度な美肌加工や歪みを避け、自然で信頼感のある宣材写真にする。';

export const MusicianPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userPlan, isPaidUser, isLoading: authLoading, forceSyncCredits: syncCredits } = useAuth();
  const remainingCredits = userPlan?.creditsRest ?? 0;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);

  const [currentStep, setCurrentStep] = useState<Step>('upload');
  const [originalImage, setOriginalImage] = useState<ImageData>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  const currentGenerationIdRef = useRef(0);

  // Musician options
  const [backgroundId, setBackgroundId] = useState('studio_white');
  const [customBackgroundText, setCustomBackgroundText] = useState('');
  const [customBackgroundImage, setCustomBackgroundImage] = useState<ImageData>(null);
  const [dressId, setDressId] = useState('dress_black');
  const [customDressText, setCustomDressText] = useState('');
  const [customDressImage, setCustomDressImage] = useState<ImageData>(null);
  const [sleeveId, setSleeveId] = useState('any');
  const [customSleeveText, setCustomSleeveText] = useState('');
  const [itemId, setItemId] = useState('none');
  const [customItemText, setCustomItemText] = useState('');
  const [poseId, setPoseId] = useState('front');
  const [inheritedStyle, setInheritedStyle] = useState<InheritedStylePayload | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inheritStyle = urlParams.get('inheritStyle');
    const showcaseId = urlParams.get('showcaseId');

    if (inheritStyle !== '1' && !showcaseId) {
      return;
    }

    let cancelled = false;

    const loadStyle = async () => {
      try {
        let payload = loadInheritedStyle();

        if ((!payload || !payload.settings) && showcaseId) {
          const item = await getShowcaseItemById(showcaseId);
          if (item) {
            payload = {
              showcaseId: item.id,
              prompt: item.prompt,
              imageUrl: item.imageUrl,
              settings: item.settings || null,
            };
            saveInheritedStyle(payload);
          }
        }

        if (cancelled || !payload) {
          return;
        }

        const settings = normalizeInheritedSettings(payload.settings);
        if (settings.mode !== 'musician') {
          window.location.replace(`/?mode=app&inheritStyle=1${showcaseId ? `&showcaseId=${encodeURIComponent(showcaseId)}` : ''}`);
          return;
        }

        setBackgroundId(settings.backgroundId || 'studio_white');
        setDressId(settings.dressId || 'dress_black');
        setSleeveId(settings.sleeveId || 'any');
        setItemId(settings.itemId || 'none');
        setPoseId(settings.poseId || 'front');
        setCustomBackgroundText(settings.customBackgroundText || '');
        setCustomDressText(settings.customDressText || '');
        setCustomSleeveText(settings.customSleeveText || '');
        setCustomItemText(settings.customItemText || '');
        setCustomBackgroundImage(null);
        setCustomDressImage(null);
        setInheritedStyle(payload);
        setCurrentStep('upload');

        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (error) {
        console.error('音楽家スタイルの引き継ぎに失敗:', error);
      }
    };

    loadStyle();

    return () => {
      cancelled = true;
    };
  }, []);

  const buildMusicianPrompt = (
    custBgImage: ImageData,
    custDressImage: ImageData
  ): string => {
    let p = MUSICIAN_BASE_PROMPT;

    const bg = getMusicianBackgroundById(backgroundId);
    if (backgroundId === 'custom_text' && customBackgroundText.trim()) {
      p += ` 背景: ${customBackgroundText.trim()}`;
    } else if (backgroundId === 'custom_image' && custBgImage) {
      p += ' 背景: 添付画像の環境を参考';
    } else if (bg?.prompt) {
      p += ` 背景: ${bg.prompt}`;
    } else {
      p += ' 背景: 白いスタジオ';
    }

    const dress = getMusicianDressById(dressId);
    if (dressId === 'custom_text' && customDressText.trim()) {
      p += ` 衣装: ${customDressText.trim()}`;
    } else if (dressId === 'custom_image' && custDressImage) {
      p += ' 衣装: 添付画像のスタイル';
    } else if (dress?.prompt) {
      p += ` 衣装: ${dress.prompt}`;
    }

    const sleeve = getMusicianSleeveById(sleeveId);
    if (sleeveId === 'custom_text' && customSleeveText.trim()) {
      p += ` 袖: ${customSleeveText.trim()}`;
    } else if (sleeve?.prompt) {
      p += ` 袖: ${sleeve.prompt}`;
    }

    const item = getMusicianItemById(itemId);
    if (itemId === 'custom_text' && customItemText.trim()) {
      p += ` 楽器・アイテム: ${customItemText.trim()}`;
    } else if (item?.prompt) {
      p += ` ${item.prompt}`;
    }

    const pose = getMusicianPoseById(poseId);
    if (pose?.prompt) {
      p += ` ${pose.prompt}`;
    }

    return p;
  };

  const handleImageUpload = (data: string, mimeType: string) => {
    setOriginalImage({ data, mimeType });
    setGeneratedImage(null);
    setError(null);
    setCurrentStep('template');
  };

  const handleReset = () => {
    setOriginalImage(null);
    setGeneratedImage(null);
    setError(null);
    setIsGenerating(false);
    setGenerationProgress(0);
    setGenerationStep('');
    setCurrentStep('upload');
    setBackgroundId('studio_white');
    setCustomBackgroundText('');
    setCustomBackgroundImage(null);
    setDressId('dress_black');
    setCustomDressText('');
    setCustomDressImage(null);
    setSleeveId('any');
    setCustomSleeveText('');
    setItemId('none');
    setCustomItemText('');
    setPoseId('front');
  };

  const handleGenerate = async (customOutfit: ImageData, customBackground: ImageData) => {
    if (!originalImage || !user) return;
    if (isGenerating) return;
    if (remainingCredits <= 0) {
      setError('クレジットが不足しています。有料プランをご検討ください。');
      return;
    }

    const generationId = ++currentGenerationIdRef.current;
    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);
    setCurrentStep('generate');
    setGenerationProgress(0);
    setGenerationStep('画像を分析中...');
    await new Promise((r) => setTimeout(r, 100));

    try {
      const finalPrompt = buildMusicianPrompt(customBackground, customOutfit);
      setCurrentPrompt(finalPrompt);

      if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
        throw new Error('ネットワーク接続がありません。');
      }

      const result = await generateHeadshot(
        originalImage.data,
        originalImage.mimeType,
        finalPrompt,
        customOutfit,
        customBackground
      );

      if (generationId !== currentGenerationIdRef.current) return;
      if (!result) throw new Error('画像生成に失敗しました');

      setGeneratedImage(result);
      setCurrentStep('result');

      if (user) {
        try {
          let finalImageForSave = result;
          if (!isPaidUser) {
            try {
              const { downscaleImage } = await import('../utils/watermarkUtils');
              finalImageForSave = await downscaleImage(result, 0.5);
            } catch {
              finalImageForSave = result;
            }
          }
          const settings = {
            mode: 'musician',
            backgroundId,
            dressId,
            sleeveId,
            itemId,
            poseId,
            customBackgroundText: backgroundId === 'custom_text' ? customBackgroundText : undefined,
            customDressText: dressId === 'custom_text' ? customDressText : undefined,
            originalImageMimeType: originalImage.mimeType,
          };
          await updateCreditsAndSaveImage(
            user.uid,
            finalImageForSave,
            originalImage.mimeType,
            finalPrompt,
            'musician',
            dressId,
            poseId,
            backgroundId === 'custom_text' ? customBackgroundText : undefined,
            settings
          );
          await syncCredits?.();
        } catch (saveErr: unknown) {
          console.warn('保存エラー（画像は表示されます）:', saveErr);
        }
      }
    } catch (e: unknown) {
      if (generationId !== currentGenerationIdRef.current) return;
      const msg = e instanceof Error ? e.message : '画像生成中にエラーが発生しました';
      setError(msg);
      setCurrentStep('template');
    } finally {
      if (generationId !== currentGenerationIdRef.current) return;
      setIsGenerating(false);
      setGenerationProgress(0);
      setGenerationStep('');
    }
  };

  // Progress UI during generation
  useEffect(() => {
    if (!isGenerating) return;
    const steps = [
      { progress: 20, text: '画像を分析中...' },
      { progress: 40, text: 'AIが生成中...' },
      { progress: 60, text: '画像を処理中...' },
      { progress: 80, text: '最終調整中...' },
      { progress: 95, text: '仕上げ中...' },
    ];
    let i = 0;
    const iv = setInterval(() => {
      if (i < steps.length) {
        setGenerationProgress(steps[i].progress);
        setGenerationStep(steps[i].text);
        i++;
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [isGenerating]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans antialiased flex items-center justify-center">
        <p className="text-slate-600">読み込み中...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white font-sans antialiased">
        <Header onGoHome={() => navigate('/')} variant="musician" />
        
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-slate-50 py-20 sm:py-32">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-6xl mb-6">
              スマホの自撮りが、<br />
              一瞬で<span className="text-blue-600">「演奏会向けの宣材写真」</span>に。
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 max-w-2xl mx-auto">
              スタジオ予約も、ドレスの準備もいりません。<br />
              あなたのスナップ写真から、プログラムに載せられるクオリティの<br />
              音楽家プロフィール写真をAIが作成します。
            </p>
            
            {/* ビジュアルエリア：Before/After */}
            <div className="mt-16 relative mx-auto max-w-5xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Before: 私服の自撮り */}
                <div className="relative rounded-xl overflow-hidden shadow-lg bg-white">
                  <div className="absolute top-3 left-3 z-10 bg-slate-900/70 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Before
                  </div>
                  <img
                    src={musicianBeforeImg}
                    alt="私服の自撮り"
                    className="w-full h-full object-cover aspect-[3/4]"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="w-full aspect-[3/4] flex items-center justify-center bg-slate-100 text-slate-500 p-4 text-center">私服の自撮り画像</div>';
                      }
                    }}
                  />
                </div>
                
                {/* After: ドレス姿の演奏家 */}
                <div className="relative rounded-xl overflow-hidden shadow-lg bg-white">
                  <div className="absolute top-3 left-3 z-10 bg-blue-600/90 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    After
                  </div>
                  <img
                    src={musicianAfterImg}
                    alt="ドレス姿の演奏家"
                    className="w-full h-full object-cover aspect-[3/4]"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="w-full aspect-[3/4] flex items-center justify-center bg-slate-100 text-slate-500 p-4 text-center">ドレス姿の演奏家画像</div>';
                      }
                    }}
                  />
                </div>
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">スマホの自撮り</span> → <span className="font-semibold text-blue-600">演奏会向けの宣材写真</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center lg:max-w-4xl">
              <h2 className="text-base font-semibold leading-7 text-blue-600">Features</h2>
              <p className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 lg:text-4xl">
                演奏家のためのこだわり機能
              </p>
            </div>
            <div className="mx-auto mt-16 sm:mt-20 lg:mt-24 max-w-2xl sm:max-w-none">
              <dl className="grid grid-cols-1 gap-8 sm:gap-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-12">
                
                {/* Feature 1 */}
                <div className="relative">
                  <dt className="text-base font-semibold leading-7 text-slate-900 mb-2">
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-xl sm:text-2xl">
                      👗
                    </div>
                    ドレス・衣装も自由自在
                  </dt>
                  <dd className="text-sm sm:text-base leading-6 sm:leading-7 text-slate-600">
                    オフショルダー、長袖レースなど、季節や曲目に合わせて衣装の袖丈まで調整可能です。
                  </dd>
                </div>

                {/* Feature 2 */}
                <div className="relative">
                  <dt className="text-base font-semibold leading-7 text-slate-900 mb-2">
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-xl sm:text-2xl">
                      🎻
                    </div>
                    楽器と一緒に
                  </dt>
                  <dd className="text-sm sm:text-base leading-6 sm:leading-7 text-slate-600">
                    バイオリンやフルートなど、楽器を持った自然なポージングを生成。演奏家らしい一枚に。
                  </dd>
                </div>

                {/* Feature 3 */}
                <div className="relative md:col-span-2 lg:col-span-1">
                  <dt className="text-base font-semibold leading-7 text-slate-900 mb-2">
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-xl sm:text-2xl">
                      📸
                    </div>
                    印刷品質の高解像度
                  </dt>
                  <dd className="text-sm sm:text-base leading-6 sm:leading-7 text-slate-600">
                    コンサートのチラシやパンフレットにも耐えうる高解像度でダウンロードできます。
                  </dd>
                </div>

              </dl>
            </div>
          </div>
        </section>

        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={() => setShowLoginModal(false)}
        />
      </div>
    );
  }

  const steps: { step: Step; label: string }[] = [
    { step: 'upload', label: '写真を選ぶ' },
    { step: 'template', label: 'スタイル設定' },
    { step: 'generate', label: '生成中' },
    { step: 'result', label: '結果' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans antialiased">
      <Header
        onGoHome={() => {
          clearInheritedStyle();
          navigate('/');
        }}
        onShowGallery={() => setShowGalleryModal(true)}
        showTrialInfo={!isPaidUser}
        trialRemaining={remainingCredits}
        userPlan={userPlan}
        variant="musician"
      />
      <main className="container mx-auto px-4 md:px-8 pt-20 pb-6 md:pb-12">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">音楽家向け宣材写真</h1>
          <p className="text-slate-600 text-sm">ドレス・楽器・背景を選んで、一枚の宣材写真を生成します</p>
        </div>

        {inheritedStyle && (currentStep === 'upload' || currentStep === 'template') && (
          <div className="mb-6 flex items-start gap-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
            {inheritedStyle.imageUrl ? (
              <img
                src={inheritedStyle.imageUrl}
                alt="引き継いだスタイル"
                className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-blue-900">ショーケースのスタイルを引き継ぎました</p>
              <p className="mt-1 text-sm text-blue-800">
                自分の写真をアップロードすると、同じ雰囲気の音楽家向け宣材写真を作れます。
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setInheritedStyle(null);
                clearInheritedStyle();
              }}
              className="flex-shrink-0 rounded-lg px-2 py-1 text-sm text-blue-700 hover:bg-blue-100"
            >
              解除
            </button>
          </div>
        )}

        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 justify-center">
            {steps.map(({ step, label }, idx) => (
              <div key={step} className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === step ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {idx + 1}
                </div>
                <span className="text-sm font-medium text-slate-600">{label}</span>
                {idx < steps.length - 1 && (
                  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>

        {currentStep === 'upload' && (
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-xl font-bold text-slate-900 mb-4">ステップ1: 写真を選ぶ</h2>
            <p className="text-slate-600 mb-6">顔がはっきり写った写真をアップロードしてください</p>
            <ImageUploader
              onImageUpload={handleImageUpload}
              freeTrialUsed={remainingCredits}
              scrollToPlanInfo={() => navigate('/')}
              userPlan={userPlan}
            />
          </div>
        )}

        {currentStep === 'template' && originalImage && (
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">ステップ2: スタイル設定</h2>
              <button
                type="button"
                onClick={() => setCurrentStep('upload')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                ← 戻る
              </button>
            </div>
            <MusicianControlPanel
              onGenerate={handleGenerate}
              onReset={handleReset}
              isLoading={isGenerating}
              error={error}
              remainingCredits={remainingCredits}
              scrollToPlanInfo={() => navigate('/')}
              backgroundId={backgroundId}
              onBackgroundIdChange={setBackgroundId}
              customBackgroundText={customBackgroundText}
              onCustomBackgroundTextChange={setCustomBackgroundText}
              customBackgroundImage={customBackgroundImage}
              onCustomBackgroundImageChange={setCustomBackgroundImage}
              dressId={dressId}
              onDressIdChange={setDressId}
              customDressText={customDressText}
              onCustomDressTextChange={setCustomDressText}
              customDressImage={customDressImage}
              onCustomDressImageChange={setCustomDressImage}
              sleeveId={sleeveId}
              onSleeveIdChange={setSleeveId}
              customSleeveText={customSleeveText}
              onCustomSleeveTextChange={setCustomSleeveText}
              itemId={itemId}
              onItemIdChange={setItemId}
              customItemText={customItemText}
              onCustomItemTextChange={setCustomItemText}
              poseId={poseId}
              onPoseIdChange={setPoseId}
            />
          </div>
        )}

        {currentStep === 'generate' && (
          <div className="text-center max-w-md mx-auto">
            <h2 className="text-xl font-bold text-slate-900 mb-4">生成中...</h2>
            <div className="bg-slate-100 rounded-lg p-8">
              <p className="font-semibold text-slate-700 mb-2">{generationStep || 'AIが生成しています'}</p>
              <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${generationProgress}%` }}
                />
              </div>
              <p className="text-sm text-slate-500">{generationProgress}%</p>
            </div>
          </div>
        )}

        {currentStep === 'result' && originalImage && (
          <div className="w-full">
            <h2 className="text-xl font-bold text-slate-900 mb-4">生成完了！</h2>
            <p className="text-slate-600 mb-6">生成された宣材写真をご確認ください</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <ImageViewer
                originalImage={originalImage.data}
                generatedImage={generatedImage}
                isLoading={false}
                isPaidUser={isPaidUser ?? false}
                currentPrompt={currentPrompt}
              />
              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">次のアクション</h3>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700"
                  >
                    新しい写真を作成
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      currentGenerationIdRef.current++;
                      setIsGenerating(false);
                      setError(null);
                      setGenerationProgress(0);
                      setGenerationStep('');
                      setCurrentStep('template');
                    }}
                    className="w-full bg-slate-200 text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-300"
                  >
                    設定を変更して再生成
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="w-full border border-slate-300 text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-50"
                  >
                    ホームへ戻る
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <GalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
      />
    </div>
  );
};
